import { jwtDecode } from 'jwt-decode'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { SigninResponse } from '@/features/auth/types'

interface DecodedToken {
  exp: number
  iat: number
  sub?: string
  email?: string
  userId?: string
}

interface AuthState {
  // State
  isAuthenticated: boolean
  user: SigninResponse['user'] | null
  accessToken: string | null
  refreshToken: string | null
  isLoading: boolean

  // Actions
  setAuth: (user: SigninResponse['user'], accessToken: string, refreshToken: string) => void
  setUser: (user: SigninResponse['user'] | null) => void
  clearAuth: () => void
  updateTokens: (accessToken: string, refreshToken: string) => void
  setLoading: (isLoading: boolean) => void
  checkAuthStatus: () => boolean
  initializeAuth: () => void

  // Token utilities
  isTokenExpired: (token: string) => boolean
  isTokenExpiringSoon: (token: string, bufferSeconds?: number) => boolean
  getStoredTokens: () => { accessToken: string | null; refreshToken: string | null }
}

// Token storage keys
const ACCESS_TOKEN_KEY = 'accessToken'
const REFRESH_TOKEN_KEY = 'refreshToken'

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        isLoading: true,

        // Set authentication data
        setAuth: (user, accessToken, refreshToken) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)

          set({
            accessToken,
            refreshToken,
            isLoading: false,
          })

          get().setUser(user)
        },

        setUser: user => {
          if (!user) {
            localStorage.removeItem('user')
            set({
              user: null,
              isAuthenticated: false,
            })
            return
          }

          localStorage.setItem('user', JSON.stringify(user))

          const hasTokens =
            Boolean(get().accessToken && get().refreshToken) ||
            Boolean(
              localStorage.getItem(ACCESS_TOKEN_KEY) && localStorage.getItem(REFRESH_TOKEN_KEY)
            )

          set({
            user,
            isAuthenticated: hasTokens,
          })
        },

        // Clear authentication data
        clearAuth: () => {
          // Clear localStorage
          localStorage.removeItem(ACCESS_TOKEN_KEY)
          localStorage.removeItem(REFRESH_TOKEN_KEY)
          localStorage.removeItem('user')
          localStorage.removeItem('user_id')
          localStorage.removeItem('userId')
          localStorage.removeItem('x_auth_state')
          localStorage.removeItem('tiktok_auth_state')
          localStorage.removeItem('instagram_user_id')

          set({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            isLoading: false,
          })
        },

        // Update only tokens
        updateTokens: (accessToken, refreshToken) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)

          set({
            accessToken,
            refreshToken,
          })
        },

        // Set loading state
        setLoading: isLoading => set({ isLoading }),

        // Check if current auth is valid
        checkAuthStatus: () => {
          const state = get()

          if (!state.accessToken || !state.user) {
            return false
          }

          // Check if token is expired
          if (get().isTokenExpired(state.accessToken)) {
            get().clearAuth()
            return false
          }

          return true
        },

        // Initialize auth from localStorage
        initializeAuth: () => {
          const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
          const userStr = localStorage.getItem('user')

          if (!accessToken || !refreshToken || !userStr) {
            set({ isLoading: false, isAuthenticated: false })
            return
          }

          try {
            const user = JSON.parse(userStr)

            // Check if token is valid
            if (get().isTokenExpired(accessToken)) {
              get().clearAuth()
              return
            }

            set({
              isAuthenticated: true,
              user,
              accessToken,
              refreshToken,
              isLoading: false,
            })
          } catch (error) {
            console.error('Failed to parse stored auth data:', error)
            get().clearAuth()
          }
        },

        // Token validation functions
        isTokenExpired: (token: string): boolean => {
          try {
            const decoded = jwtDecode<DecodedToken>(token)
            const currentTime = Date.now() / 1000
            return decoded.exp < currentTime
          } catch {
            return true
          }
        },

        isTokenExpiringSoon: (token: string, bufferSeconds = 60): boolean => {
          try {
            const decoded = jwtDecode<DecodedToken>(token)
            const currentTime = Date.now() / 1000
            return decoded.exp - currentTime < bufferSeconds
          } catch {
            return true
          }
        },

        // Get tokens from localStorage
        getStoredTokens: () => ({
          accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
          refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
        }),
      }),
      {
        name: 'auth-storage',
        partialize: state => ({
          // We don't persist tokens directly in zustand
          // They are managed by localStorage
          isAuthenticated: state.isAuthenticated,
          user: state.user,
        }),
      }
    )
  )
)

// Initialize auth on app start
if (typeof window !== 'undefined') {
  useAuthStore.getState().initializeAuth()
}
